import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  fetchDrugs,
  fetchDrugsCustom,
} from "../../../../../redux/modules/clinical-services/actions";
import AsyncCreatableSelect from "react-select/async-creatable";
import Select, { components as RSComponents } from "react-select";
import { ReactSVG } from "react-svg";
import { medicationFilterOptions } from "../../service-steps/clinical-assessment/data";
import CommonSelect from "./common-creatable-select";
import NewToolTip from "../../../../common/New-ToolTip";

export const customReactSelectStyles = (isRequired, selectedOption) => ({
  control: (provided, state) => ({
    ...provided,
    border: state.isFocused ? "0px solid #2196F3" : "0px solid #E0E0E0",
    boxShadow: state.isFocused ? "none" : "none",
    "&:hover": {
      border: state.isFocused ? "0px solid #2196F3" : "0px solid #BDBDBD",
    },
    backgroundColor:
      isRequired && selectedOption === null ? "blue-100" : "white",
    borderRadius: "0.375rem",
    padding: "0px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "30px",
    minHeight: "0px",
    fontSize: "16px",
    lineHeight: "20px",
  }),
  input: (provided) => ({
    ...provided,
    padding: "0px",
    margin: "0px",
    "input:focus": {
      boxShadow: "none",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#757575",
    margin: "0px",
    fontSize: "16px",
    lineHeight: "20px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0",
    margin: "0",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#212121",
    margin: "0",
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    transition: "all .2s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : null,
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.375rem",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
    margin: "14px -12px 0 -12px",
    width: "calc(100% + 24px)",
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0",
    maxHeight: "300px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.data?.__isNew__
      ? "#FFFDED" // Light yellow for "Create" options
      : state.isSelected
      ? "#E3F2FD"
      : state.isFocused
      ? "#E3F2FD"
      : "white",
    color: state.isSelected ? "#212121" : "#212121",
    padding: "0.75rem 1rem",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: state.data?.__isNew__
        ? "#FEF9C3" // yellow-75 - slightly darker than yellow-60 on hover
        : state.isSelected 
        ? "#EEEEEE" 
        : "#EEEEEE",
    },
    fontSize: "16px",
    lineHeight: "20px",
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    padding: "1rem",
    color: "#757575",
  }),
  group: (provided) => ({
    ...provided,
    padding: "0",
  }),
  groupHeading: (provided, state) => {
    if (state?.data?.hideHeading) {
      return {
        ...provided,
        display: "none",
      };
    }

    if (state?.data?.showDivider) {
      return {
        ...provided,
        padding: "0",
        margin: "4px 0",
        borderTop: "1px solid #E0E0E0",
        height: "0",
        backgroundColor: "transparent",
      };
    }

    return {
      ...provided,
      backgroundColor: "#f5f5f5",
      color: "#212121",
      fontWeight: "bold",
      padding: "0.5rem 1rem",
      fontSize: "14px",
      lineHeight: "20px",
    };
  },
  groupList: (provided) => ({
    ...provided,
    padding: "0",
  }),
});

const measureTextPx = (() => {
  let canvas, ctx;
  return (
    text,
    font = "14px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
  ) => {
    if (!canvas) {
      canvas = document.createElement("canvas");
      ctx = canvas.getContext("2d");
    }
    ctx.font = font;
    return ctx.measureText(String(text)).width;
  };
})();

const truncateToPx = (() => {
  let canvas, ctx;
  return (
    text,
    maxWidthPx,
    font = "14px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
  ) => {
    if (!text) return { out: "", truncated: false };
    if (!canvas) {
      canvas = document.createElement("canvas");
      ctx = canvas.getContext("2d");
    }
    ctx.font = font;

    const fullW = ctx.measureText(text).width;
    if (fullW <= maxWidthPx) return { out: text, truncated: false };

    const ell = "…";
    const ellW = ctx.measureText(ell).width;

    let lo = 0,
      hi = text.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const w = ctx.measureText(text.slice(0, mid)).width + ellW;
      if (w <= maxWidthPx) lo = mid + 1;
      else hi = mid;
    }
    const cut = Math.max(0, lo - 1);
    return { out: text.slice(0, cut) + ell, truncated: true };
  };
})();

const GROUP_LABEL_MAP = {
  Gen: "Generic (Product)",
  Generx: "Generic (Generx)",
  Din: "DIN",
  Brand: "Brand",
  Sentence: "Drug Sentence",
};

const groupByType = (data, activeFilterValue) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }
  
  const groupedData = data.reduce((acc, item) => {
    const group = item.type || "Other";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push({
      ...item,
      value: item.identifier,
      label: item.displayText,
    });
    return acc;
  }, {});

  return Object.keys(groupedData).map((key) => {
    // When a specific filter is active (not All), use its display name as the group heading
    const overrideLabel =
      activeFilterValue && activeFilterValue !== "All"
        ? (GROUP_LABEL_MAP[activeFilterValue] || activeFilterValue)
        : (GROUP_LABEL_MAP[key] || key);
    return {
      label: overrideLabel,
      options: groupedData[key],
    };
  });
};

const IndicatorsContainerWithClear = (props) => {
  const { hasTypingInput, onClearTyping } = props.selectProps;
  return (
    <RSComponents.IndicatorsContainer {...props}>
      {hasTypingInput && !props.hasValue && (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClearTyping();
          }}
          className="flex items-center px-1 cursor-pointer text-gray-400 hover:text-gray-700"
          aria-label="Clear search"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </div>
      )}
      {props.children}
    </RSComponents.IndicatorsContainer>
  );
};

const PROVINCE_MAP = {
  ontario: "Ontario",
  alberta: "Alberta",
  britishColumbia: "British Columbia",
  manitoba: "Manitoba",
  newBrunswick: "New Brunswick",
  newfoundland: "Newfoundland",
  nihb: "NIHB",
  novaScotia: "Nova Scotia",
  princeEdwardIsland: "Prince Edward Island",
  ramq: "RAMQ",
  saskatchewan: "Saskatchewan",
};

const PROVINCE_PROGRAM = {
  ontario: "Ontario Drug Benefit",
  alberta: "Alberta Drug Benefit",
  britishColumbia: "BC PharmaCare",
  manitoba: "Manitoba Drug Benefits",
  newBrunswick: "NB Drug Plan",
  newfoundland: "NL Prescription Drug",
  nihb: "NIHB",
  novaScotia: "NS Formulary",
  princeEdwardIsland: "PEI Pharmacare",
  ramq: "RAMQ",
  saskatchewan: "SK Drug Plan",
};

const getCoverageLabel = (status, province) => {
  const program = PROVINCE_PROGRAM[province] ?? PROVINCE_MAP[province] ?? province;
  return {
    Covered: { text: `Covered by ${program}`, colorClass: "text-green-600" },
    NotCovered: { text: `Not Covered by ${program}`, colorClass: "text-red-500" },
    ConditionallyCovered: { text: "Restricted Coverage - See Criteria", colorClass: "text-blue-500" },
  }[status];
};

const CoverageStatusIcon = ({ status, size = 14 }) => {
  if (status === "Covered") return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="#22C55E"/>
      <path d="M4.5 8.5L6.5 10.5L11.5 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  if (status === "ConditionallyCovered") return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="#3B82F6"/>
      <path d="M6 8H10M10 8L8 6M10 8L8 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  if (status === "NotCovered") return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="#EF4444"/>
      <path d="M5 5L11 11M11 5L5 11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
  return null;
};

const MedicationSelect = ({
  type = "Medication",
  onChange = () => {},
  isRequired = false,
  onNewCreate = () => {},
  error = false,
  value = { value: "", label: "" },
  customEndpoint = false,
  defaultOptions = [],
  showDin = false,
  isDisabled = false,
  strength = null,
  viewCoverage = false,
  topSlot = null,
  /** When true, Search Filter label shows a required asterisk (AMR vaccine form). */
  searchFilterRequired = false,
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const timeoutRef = useRef(null);
  const selectContainerRef = useRef(null);
  const [drugTypeFilter, setDrugTypeFilter] = useState(medicationFilterOptions[0].options[0]);
  const [manufacturerDisplay, setManufacturerDisplay] = useState(medicationFilterOptions[1].options[0]);
  const [options, setOptions] = useState(defaultOptions);
  const [showError, setShowError] = useState(false);
  const [inputString, setInputString] = useState("");
  const selectWidthRef = useRef(null);
  const [selectWidth, setSelectWidth] = useState(0);
  const [viewCoverageActive, setViewCoverageActive] = useState(false);
  const [coverageData, setCoverageData] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState("ontario");
  const [showDetails, setShowDetails] = useState(false);
  const [showProvinceSelector, setShowProvinceSelector] = useState(false);

  const dispatch = useDispatch();

  useLayoutEffect(() => {
    const el = selectWidthRef.current;
    if (!el) return;

    const readWidth = () => setSelectWidth(el.getBoundingClientRect().width);
    readWidth();

    // Observe element resize
    const ro = new ResizeObserver(readWidth);
    ro.observe(el);

    // Also re-measure once fonts are ready
    const fontReady = document.fonts?.ready;
    if (fontReady) fontReady.then(readWidth);

    // Fallback: window resize
    window.addEventListener("resize", readWidth);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", readWidth);
    };
  }, []);

  const fetchMedicines = async (inputValue) => {
    const searchText = inputValue == null ? "" : String(inputValue);
    const query = searchText.toLowerCase();

    const filteredDefaultOptions = options.filter((option) =>
      String(option?.label ?? "")
        .toLowerCase()
        .includes(query)
    );

    if (!searchText) {
      return filteredDefaultOptions;
    }

    let formattedResults = [];

    if (customEndpoint) {
      let results = await dispatch(
        fetchDrugsCustom(customEndpoint, inputValue, drugTypeFilter.value, 100, strength)
      );
      // if (drugTypeFilter.value === "All") {
      //   const tagged = (results || []).map((item) =>
      //     item.isBrand ? { ...item, type: "Brand" } : item
      //   );
      //   formattedResults = groupByType(tagged, "All");
      // } else {
      //   formattedResults = groupByType(results || [], drugTypeFilter.value);
      // }
      formattedResults = (results || []).map((item) => ({
        ...item,
        value: item.identifier,
        label: item.displayText,
      }));
    } else {
      if (drugTypeFilter.value === "All") {
        const allResults = await dispatch(fetchDrugs(inputValue, "All"));
        // Tag brand items using the isBrand flag so they form their own group
        const tagged = (allResults || []).map((item) =>
          item.isBrand ? { ...item, type: "Brand" } : item
        );
        // formattedResults = groupByType(tagged, "All");
        formattedResults = tagged.map((item) => ({
          ...item,
          value: item.identifier,
          label: item.displayText,
        }));
      } else {
        let results = await dispatch(
          fetchDrugs(inputValue, drugTypeFilter.value)
        );
        // formattedResults = groupByType(results || [], drugTypeFilter.value);
        formattedResults = (results || []).map((item) => ({
          ...item,
          value: item.identifier,
          label: item.displayText,
        }));
      }
    }

    const combinedOptions = [
      ...filteredDefaultOptions,
      ...formattedResults.filter(
        (result) =>
          !filteredDefaultOptions.some(
            (option) => option.value === result.value
          )
      ),
    ];

    // Persist fetched options
    setOptions(() => [...formattedResults]);

    return combinedOptions;
  };

  const promiseOptions = (inputValue) =>
    new Promise((resolve) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(async () => {
        resolve(await fetchMedicines(inputValue));
      }, 300);
    });

  const handleCreate = (inputValue) => {
    setTimeout(() => {
      const newOption = {
        label: inputValue,
        value: inputValue,
      };
      setOptions((prevOptions) => [...prevOptions, newOption]);
      setSelectedOption(newOption);
      setShowError(isRequired && !newOption.value);
      onChange(newOption);
      onNewCreate(true);
    }, 300);
  };

  const handleChange = (selectedOption) => {
    const finalSelectedOption = {
      ...selectedOption,
      manufacturerStoredType:
        manufacturerDisplay?.value === "Short" ? "Small" : "Full",
    };
    // Mark as seen so the value-prop effect doesn't trigger a re-search
    prevValueRef.current = finalSelectedOption?.value ?? null;
    setCoverageData(finalSelectedOption?.coverage ?? null);
    setSelectedOption(finalSelectedOption);
    setShowError(isRequired && !finalSelectedOption);
    onChange(finalSelectedOption);
    if (finalSelectedOption && finalSelectedOption.value) {
      onNewCreate(false);
    }
    setInputString("");
  };

  // When a drug is pre-selected from the JSON (value prop changes from parent),
  // do a one-time search to populate the dropdown with related results.
  const prevValueRef = useRef(null);
  useEffect(() => {
    const incoming = value?.value;
    if (incoming && incoming !== prevValueRef.current && value?.label) {
      prevValueRef.current = incoming;
      promiseOptions(value.label.slice(0, 10));
    }
  }, [value]);

  const handleMenuOpen = () => {
    setTimeout(() => {
      if (!selectContainerRef.current) return;
      const selected = selectContainerRef.current.querySelector('[aria-selected="true"]');
      if (selected) selected.scrollIntoView({ block: "nearest" });
    }, 0);
  };

  const openVigilanceCoverage = async (dinOverride) => {
    const vigilanceEnv = import.meta.env.VITE_VIGILANCE_ENV || "int";
    const rxVigilanceBaseUrl = `https://rx.${vigilanceEnv}.vigilance.ca`;
    const rxVigilanceAuthUrl = `https://rxauth.${vigilanceEnv}.vigilance.ca`;
    try {
      const tokenResponse = await axios.post(
        `${rxVigilanceAuthUrl}/token`,
        new URLSearchParams({
          client_id: import.meta.env.VITE_APP_VIGILANCE_ID,
          client_secret: import.meta.env.VITE_APP_VIGILANCE_SECRET,
          user_id: "REEL01",
          grant_type: "client_credentials",
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      const token = tokenResponse.data.access_token;
      const din = dinOverride ?? selectedOption?.identifier ?? null;
      const intrant = {
        query: {
          service: {
            id: "informationAndCoverage",
            ...(din ? { config: { targetMedication: { p: 0 } } } : {}),
          },
        },
        profile: {
          patient: {},
          ...(din ? {
            medications: [
              { product: [{ code: din.trim(), fmt: "DIN" }] },
            ],
          } : {}),
        },
        viewerConfig: { language: "en" },
      };
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${rxVigilanceBaseUrl}/module/couverture/couverture-ndx.html`;
      form.target = "_blank";
      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden"; tokenInput.name = "token"; tokenInput.value = token;
      const intrantInput = document.createElement("input");
      intrantInput.type = "hidden"; intrantInput.name = "intrant"; intrantInput.value = JSON.stringify(intrant);
      form.appendChild(tokenInput); form.appendChild(intrantInput);
      document.body.appendChild(form); form.submit(); document.body.removeChild(form);
    } catch (err) {
      console.error("Vigilance coverage error:", err);
    }
  };

  const isManuEnabled = (dtValue) =>
    ["All", "Brand", "Din"].includes(dtValue);

  console.log(selectedOption, "selectedOption")

  const handleFilterChange = (opt) => {
    if (!opt) return;
    if (opt.group === "drugType") {
      setDrugTypeFilter(opt);
      if (opt.value === "All" || opt.value === "Din" || opt.value === "Brand") {
        setManufacturerDisplay(medicationFilterOptions[1].options[0]); // Short Code
      } else {
        setManufacturerDisplay(medicationFilterOptions[1].options[2]); // None
      }
    } else {
      // ignore clicks when manufacturer section is locked
      if (!isManuEnabled(drugTypeFilter.value)) return;
      setManufacturerDisplay(opt);
    }
  };

  useEffect(() => {
    if (inputString.length !== 0) {
      promiseOptions(inputString);
    }
  }, [drugTypeFilter]);

  useEffect(() => {
    if (value?.value) {
      setSelectedOption(value);
      // Only update coverage from value prop for external/JSON pre-selections.
      // When the user picks manually, handleChange already set coverageData — don't overwrite.
      if (value.value !== prevValueRef.current) {
        setCoverageData(value.coverage ?? null);
      }
    } else {
      setSelectedOption(null);
      setCoverageData(null);
    }
  }, [value]);

  useEffect(() => {
    setShowError(error);
  }, [error]);

  const getCoverageIcon = (coverage, id) => {
    if (!coverage) return null;
    const status = coverage.ontario;
    const tipId = id ? `cov-${id}` : null;

    const iconMap = {
      Covered: {
        label: "Covered by Ontario Drug Benefit",
        svg: (
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="8" fill="#22C55E"/>
            <path d="M4.5 8.5L6.5 10.5L11.5 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
      },
      ConditionallyCovered: {
        label: "Restricted Coverage - See Criteria",
        svg: (
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="8" fill="#3B82F6"/>
            <path d="M6 8H10M10 8L8 6M10 8L8 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
      },
      NotCovered: {
        label: "Not Covered by Ontario Drug Benefit",
        svg: (
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="8" fill="#EF4444"/>
            <path d="M5 5L11 11M11 5L5 11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        ),
      },
    };

    const entry = iconMap[status];
    if (!entry) return null;

    return (
      <>
        <span
          className="shrink-0 flex items-center cursor-default"
          {...(tipId ? { "data-tooltip-id": tipId } : {})}
        >
          {entry.svg}
        </span>
        {tipId && (
          <NewToolTip
            id={tipId}
            place="bottom"
            render={() => (
              <div className="text-teal-300 p-4 bg-gray-10">
                <p>{entry.label}</p>
              </div>
            )}
          />
        )}
      </>
    );
  };

  const formatOptionLabel = (props, { context } = {}) => {
    const { label, type, identifier, manufacturerCode, manufacturerName, coverage } =
      props;
    const showManufacturer = manufacturerDisplay?.value === "Short";
    const showManufacturerName = manufacturerDisplay?.value === "Full";

    // Decide what to append based on filter
    const showCode = showManufacturer && manufacturerCode;

    const showName = showManufacturerName && manufacturerName;

    const appended = showCode
      ? ` (${manufacturerCode})`
      : showName
      ? ` (${manufacturerName})`
      : "";

    const fullText = `${label}${appended}`;

    // Estimate remaining space for the LEFT label column inside the option row
    // Tune these constants if your styles change
    const PADDING_LR = 24; // px; wrapper/internal x-padding overall
    const DROPDOWN_ICON = 20; // px; chevron area
    const SAFETY = 12; // px; buffer so we don't wrap
    const dinWidth =
      identifier && type === "Din" && showDin && drugTypeFilter.value !== "Brand"
        ? measureTextPx(String(identifier)) + 8 // +8 small gap
        : 0;

    const usablePx = Math.max(
      0,
      selectWidth - PADDING_LR - DROPDOWN_ICON - dinWidth - SAFETY
    );

    const { out: displayText, truncated: needsEllipsis } =
      usablePx > 0
        ? truncateToPx(fullText, usablePx)
        : { out: fullText, truncated: false };

    const tooltipId = `med-opt-${type}-${identifier ?? label}`
      .replace(/\s+/g, "-")
      .toLowerCase();

    return (
      <div className="group/opt flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {viewCoverage && getCoverageIcon(coverage, tooltipId)}
          <p
            className="relative flex gap-2 truncate"
            data-tooltip-id={needsEllipsis ? tooltipId : undefined}
          >
            {displayText}
          </p>

          {needsEllipsis && (
            <NewToolTip
              id={tooltipId}
              place="bottom"
              render={() => (
                <div className="max-w-[28rem] p-2 text-sm text-black z-[1000000] relative bg-white">
                  {fullText}
                </div>
              )}
            />
          )}
        </div>

        {/* Hover info icon — opens Vigilance for drugs with coverage */}
        {viewCoverage && coverage && context === "menu" && (
          <button
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); openVigilanceCoverage(identifier); }}
            className="opacity-0 group-hover/opt:opacity-100 shrink-0 transition-all p-1 rounded border border-transparent hover:border-REELTeal hover:bg-teal-25"
            title="View Drug Coverage"
          >
            <img src="/icons/info-coverage.svg" alt="View Coverage" style={{ width: 24, height: 24, display: "block" }} />
          </button>
        )}
        {identifier && type === "Din" && showDin && drugTypeFilter.value !== "Brand" && (
          <p className="shrink-0">{identifier}</p>
        )}
      </div>
    );
  };

  return (
    <div className="w-full col-span-12 grid grid-cols-12 gap-3">
      {/* When topSlot is provided: render topSlot (col-8) + Search Filter (col-4) in row 1 */}
      {topSlot && !customEndpoint && (
        <>
          <div className="col-span-8 h-full">{topSlot}</div>
          <div className={`col-span-4 flex flex-col h-full ${isDisabled ? "pointer-events-none opacity-70" : ""}`}>
            <div className="flex flex-col h-full relative rounded-[4px] border-[1px] border-gray-100 px-3 py-3 focus-within:border-REELTeal focus-within:border-[2px]">
              <span className="flex items-center gap-1 mb-1 font-normal text-gray-900 text-xs leading-[18px]">
                Search Filter
                <span data-tooltip-id="search-filter-info-tooltip" className="inline-flex cursor-pointer">
                  <ReactSVG src="/icons/information-dark.svg" className="max-w-4 w-4" />
                </span>
                <NewToolTip
                  id="search-filter-info-tooltip"
                  place="bottom"
                  render={() => (
                    <div className="text-teal-300 p-4 bg-gray-10">
                      <p className="text-base">Term Meanings</p>
                      <div className="border-b-[7px] border-gray-20 mb-[6px]"></div>
                      <p>
                        <span className="font-medium">Generic (Product)</span>
                        {" – "}A specific generic drug product (individual DIN/formulation)
                      </p>
                      <p className="mt-1">
                        <span className="font-medium">Generic Class (Generx)</span>
                        {" – "}The generic class or family (group identity across products)
                      </p>
                    </div>
                  )}
                />
              </span>
              <Select
                options={medicationFilterOptions}
                value={{
                  value: "__combined__",
                  label: (() => {
                    const dtLabel = drugTypeFilter.shortLabel || drugTypeFilter.label;
                    const manuLabel = manufacturerDisplay.shortLabel || manufacturerDisplay.label;
                    return manufacturerDisplay.value === "None"
                      ? dtLabel
                      : `${dtLabel} · ${manuLabel}`;
                  })(),
                }}
                onChange={handleFilterChange}
                closeMenuOnSelect={false}
                isSearchable={false}
                isDisabled={isDisabled}
                isOptionDisabled={(opt) =>
                  opt.group === "manufacturerType" && !isManuEnabled(drugTypeFilter.value)
                }
                styles={{
                  ...customReactSelectStyles(false, true),
                  groupHeading: (provided) => ({
                    ...provided,
                    backgroundColor: "#f5f5f5",
                    color: "#424242",
                    fontWeight: "700",
                    padding: "0.6rem 1rem",
                    fontSize: "13px",
                    lineHeight: "18px",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }),
                }}
                formatOptionLabel={(opt, { context }) => {
                  if (context === "value") return <span>{opt.label}</span>;
                  const isActive =
                    opt.group === "drugType"
                      ? drugTypeFilter.value === opt.value
                      : manufacturerDisplay.value === opt.value;
                  const isLocked =
                    opt.group === "manufacturerType" && !isManuEnabled(drugTypeFilter.value);
                  return (
                    <div
                      className="flex items-center justify-between gap-2"
                      style={isLocked ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
                    >
                      <span>{opt.label}</span>
                      {isActive && !isLocked && (
                        <span className="text-REELTeal font-medium text-sm">✓</span>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </>
      )}
      {/* Medication field — col-12 when topSlot, col-8 normally (col-12 if customEndpoint) */}
      <div className={`${topSlot ? "col-span-12" : (!customEndpoint ? "col-span-8" : "col-span-12")} `}>
        <div
          className={`relative rounded-[4px] border-[1px] 
            ${isDisabled ? "pointer-events-none opacity-70" : ""} 
            ${
              !isDisabled && isRequired && selectedOption === null
                ? "bg-blue-100"
                : "bg-white"
            }
                    ${showError ? "border-red-200" : "border-gray-100"}
                    px-3 py-3 focus-within:border-REELTeal focus-within:border-[2px]`}
        >
          <div ref={selectWidthRef} className="">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-900 text-body-xs">
                Medication {isRequired && <span className="">*</span>}
              </span>
              {/* View Coverage button moved into the react-select indicators area below */}
            </div>
            <div className="flex items-center gap-2">
              <div ref={selectContainerRef} className="flex-1 min-w-0 h-[30px]">
              <AsyncCreatableSelect
              key={`${drugTypeFilter.value}-${manufacturerDisplay.value}`}
              loadOptions={promiseOptions}
              isDisabled={isDisabled}
              defaultOptions={options}
              styles={customReactSelectStyles(isRequired, selectedOption)}
              value={selectedOption}
              onChange={handleChange}
              formatOptionLabel={formatOptionLabel}
              inputValue={selectedOption ? "" : (viewCoverage ? "" : inputString)}
              onInputChange={(value, action) => {
                if (viewCoverage) return;
                if (
                  action?.action !== "input-blur" &&
                  action?.action !== "menu-close"
                ) {
                  setSelectedOption(null);
                  setInputString(value);
                }
              }}
              onCreateOption={handleCreate}
              placeholder={`Select ${type}`}
              noOptionsMessage={() => "No medication found"}
              className=""
              isSearchable={!viewCoverage}
              isClearable={!viewCoverage}
              hasTypingInput={inputString.length > 0}
              onClearTyping={() => setInputString("")}
              menuIsOpen={viewCoverage ? viewCoverageActive : undefined}
              onMenuOpen={() => { if (!viewCoverage) handleMenuOpen(); else setViewCoverageActive(true); }}
              onMenuClose={() => { if (viewCoverage) setViewCoverageActive(false); }}
              components={{
                IndicatorsContainer: IndicatorsContainerWithClear,
                ...(viewCoverage ? { DropdownIndicator: () => null, IndicatorSeparator: () => null } : {}),
              }}
            />
              </div>
            </div>
            {viewCoverage && selectedOption && (
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setViewCoverageActive((v) => !v)}
                className={`absolute right-3 h-[38px] top-1/2 -translate-y-1/2  text-sm px-4 rounded border transition-colors ${
                  viewCoverageActive
                    ? "bg-REELTeal text-white border-REELTeal"
                    : "bg-gray-10 text-gray-700 border-REELTeal"
                }`}
              >
                View Coverage
              </button>
            )}
          </div>
        </div>
        {showError && (
          <span className="text-red-200 text-xs">Medication is required</span>
        )}
      </div>
      {/* When no topSlot: Search Filter renders beside Medication (original layout) */}
      {!topSlot && !customEndpoint && (
        <div className={`col-span-4 flex-col ${isDisabled ? "pointer-events-none opacity-70" : ""}`}>
          <div className="relative rounded-[4px] border-[1px] border-gray-100 px-3 py-3 focus-within:border-REELTeal focus-within:border-[2px]">
            <span className="flex items-center gap-1 mb-1 font-normal text-gray-900 text-xs leading-[18px]">
              Search Filter
              {searchFilterRequired ? <span className=""> *</span> : null}
              <span data-tooltip-id="search-filter-info-tooltip" className="inline-flex cursor-pointer">
                <ReactSVG src="/icons/information-dark.svg" className="max-w-4 w-4" />
              </span>
              <NewToolTip
                id="search-filter-info-tooltip"
                place="bottom"
                render={() => (
                  <div className="text-teal-300 p-4 bg-gray-10">
                    <p className="text-base">Term Meanings</p>
                    <div className="border-b-[7px] border-gray-20 mb-[6px]"></div>
                    <p>
                      <span className="font-medium">Generic (Product)</span>
                      {" – "}A specific generic drug product (individual DIN/formulation)
                    </p>
                    <p className="mt-1">
                      <span className="font-medium">Generic Class (Generx)</span>
                      {" – "}The generic class or family (group identity across products)
                    </p>
                  </div>
                )}
              />
            </span>
            <Select
              options={medicationFilterOptions}
              value={{
                value: "__combined__",
                label: (() => {
                  const dtLabel = drugTypeFilter.shortLabel || drugTypeFilter.label;
                  const manuLabel = manufacturerDisplay.shortLabel || manufacturerDisplay.label;
                  return manufacturerDisplay.value === "None"
                    ? dtLabel
                    : `${dtLabel} · ${manuLabel}`;
                })(),
              }}
              onChange={handleFilterChange}
              closeMenuOnSelect={false}
              isSearchable={false}
              isDisabled={isDisabled}
              isOptionDisabled={(opt) =>
                opt.group === "manufacturerType" && !isManuEnabled(drugTypeFilter.value)
              }
              styles={{
                ...customReactSelectStyles(false, true),
                groupHeading: (provided) => ({
                  ...provided,
                  backgroundColor: "#f5f5f5",
                  color: "#424242",
                  fontWeight: "700",
                  padding: "0.6rem 1rem",
                  fontSize: "13px",
                  lineHeight: "18px",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }),
              }}
              formatOptionLabel={(opt, { context }) => {
                if (context === "value") return <span>{opt.label}</span>;
                const isActive =
                  opt.group === "drugType"
                    ? drugTypeFilter.value === opt.value
                    : manufacturerDisplay.value === opt.value;
                const isLocked =
                  opt.group === "manufacturerType" && !isManuEnabled(drugTypeFilter.value);
                return (
                  <div
                    className="flex items-center justify-between gap-2"
                    style={isLocked ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
                  >
                    <span>{opt.label}</span>
                    {isActive && !isLocked && (
                      <span className="text-REELTeal font-medium text-sm">✓</span>
                    )}
                  </div>
                );
              }}
            />
          </div>
        </div>
      )}
      {/* Coverage panel — commented out for now
      {viewCoverage && viewCoverageActive && selectedOption && (
        <div className="col-span-12 border border-gray-100 rounded-[4px] px-3 py-3 bg-white">
          ...
        </div>
      )}
      */}
    </div>
  );
};

export default MedicationSelect;
